import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, ExternalLink, FileText, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API, getHeaders } from '../portalShared';

export default function DraftReview() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({});
  const [processing, setProcessing] = useState('');

  useEffect(() => { fetchDrafts(); }, []);
  const fetchDrafts = async () => {
    try {
      const { data } = await axios.get(`${API}/portal/drafts`, { headers: getHeaders() });
      setDrafts(data);
    } catch {} finally { setLoading(false); }
  };

  const approveDraft = async (id) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/workspace/${id}/approve-draft`, {}, { headers: getHeaders() });
      fetchDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const rejectDraft = async (id) => {
    if (!feedback[id]?.trim()) { toast.error('Please provide feedback for the revision request.'); return; }
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/workspace/${id}/reject-draft`, { feedback: feedback[id] }, { headers: getHeaders() });
      fetchDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  const approvePost = async (id) => {
    setProcessing(id);
    try {
      await axios.post(`${API}/portal/posts/${id}/approve`, {}, { headers: getHeaders() });
      fetchDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setProcessing(''); }
  };

  if (loading) return <p className="text-[#9CA3AF]">Loading...</p>;

  const pendingDrafts = drafts.filter(d => d.draft_status === 'submitted');
  const approvedDrafts = drafts.filter(d => d.draft_status === 'approved');
  const revisionDrafts = drafts.filter(d => d.draft_status === 'revision_requested');

  return (
    <div className="space-y-6" data-testid="draft-review-section">
      <h2 className="font-display text-xl font-bold">Draft <span className="text-[#FF4343]">Review</span></h2>

      {pendingDrafts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-[#FF4343] uppercase tracking-wider">Pending Approval ({pendingDrafts.length})</h3>
          {pendingDrafts.map(d => (
            <div key={d.id} className="bg-[#111827] border border-[#FF4343]/30 p-4" data-testid={`draft-${d.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-sm font-semibold text-white">{d.executive_name}</span>
                  <span className="text-xs text-[#6B7280] ml-2">{d.campaign_title}</span>
                </div>
                <span className="text-[10px] text-yellow-400 font-mono uppercase">Awaiting Review</span>
              </div>
              <div className="bg-white/5 p-3 mb-3 text-sm text-[#9CA3AF] whitespace-pre-wrap">{d.draft_text}</div>
              <textarea value={feedback[d.id] || ''} onChange={e => setFeedback({...feedback, [d.id]: e.target.value})} placeholder="Feedback for revision (required if rejecting)..." rows={2}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-xs placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-3" />
              <div className="flex gap-2">
                <button onClick={() => approveDraft(d.id)} disabled={processing === d.id} data-testid={`approve-draft-${d.id}`}
                  className="flex-1 flex items-center justify-center gap-1 border-2 border-[#10B981] bg-[#10B981] text-[#090E33] py-2 text-xs font-semibold hover:bg-[#059669] transition-all disabled:opacity-50">
                  <CheckCircle className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => rejectDraft(d.id)} disabled={processing === d.id} data-testid={`reject-draft-${d.id}`}
                  className="flex-1 flex items-center justify-center gap-1 border border-red-500/50 text-red-400 py-2 text-xs font-medium hover:bg-red-500/10 transition-all disabled:opacity-50">
                  <XCircle className="w-3 h-3" /> Request Revision
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {approvedDrafts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-[#10B981] uppercase tracking-wider">Approved — Awaiting Post ({approvedDrafts.length})</h3>
          {approvedDrafts.map(d => (
            <div key={d.id} className="bg-[#111827] border border-[#10B981]/20 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-white">{d.executive_name} <span className="text-xs text-[#6B7280]">{d.campaign_title}</span></span>
                {d.post_url ? (
                  <button onClick={() => approvePost(d.id)} disabled={processing === d.id} data-testid={`approve-post-${d.id}`}
                    className="text-[10px] px-3 py-1 border-2 border-[#10B981] bg-[#10B981] text-[#090E33] font-semibold hover:bg-[#059669] transition-all disabled:opacity-50">
                    Approve Post & Release Payment
                  </button>
                ) : (
                  <span className="text-[10px] text-[#6B7280] font-mono">Waiting for post</span>
                )}
              </div>
              <div className="bg-white/5 p-3 text-xs text-[#9CA3AF] whitespace-pre-wrap">{d.draft_text}</div>
              {d.post_url && (
                <a href={d.post_url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#FF4343] hover:underline mt-2 inline-flex items-center gap-1">
                  View Live Post <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {revisionDrafts.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold text-yellow-400 uppercase tracking-wider">Revision Requested ({revisionDrafts.length})</h3>
          {revisionDrafts.map(d => (
            <div key={d.id} className="bg-[#111827] border border-yellow-500/20 p-4">
              <span className="text-sm font-semibold text-white">{d.executive_name} <span className="text-xs text-[#6B7280]">{d.campaign_title}</span></span>
              <div className="bg-white/5 p-3 mt-2 text-xs text-[#9CA3AF] whitespace-pre-wrap">{d.draft_text}</div>
              <p className="text-[10px] text-yellow-400 mt-2">Feedback: {d.draft_feedback}</p>
            </div>
          ))}
        </div>
      )}

      {drafts.length === 0 && (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF] text-sm">No draft submissions yet.</p>
        </div>
      )}
    </div>
  );
}

